import React from "react";
import { useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancementTogglesPanel } from "@/components/shared/EnhancementTogglesPanel";
import { SitelinksSection } from "@/components/shared/SitelinksSection";
import type { SitelinkItem } from "@/components/shared/SitelinksSection";
import { useEnhancementConfig } from "@/hooks/useEnhancementConfig";

type EnhancementMap = Record<string, boolean>;

type CreativeEnhancementsData = {
  image_enhancements: EnhancementMap | null;
  video_enhancements: EnhancementMap | null;
  carousel_enhancements?: EnhancementMap | null;
  collection_enhancements?: Record<string, "OPT_IN" | "OPT_OUT"> | null;
  enhancements_catalog?: EnhancementMap | null;
  flexible_advantage_plus_creative?: boolean | null;
  sitelinks?: SitelinkItem[] | null;
};

interface CreativeEnhancementsStepProps {
  data: CreativeEnhancementsData;
  onChange: (data: Partial<CreativeEnhancementsData>) => void;
}

function settingsToValues(data: CreativeEnhancementsData): Record<string, boolean> {
  const values: Record<string, boolean> = {};

  for (const [k, v] of Object.entries(data.image_enhancements ?? {})) values[k] = !!v;
  for (const [k, v] of Object.entries(data.video_enhancements ?? {})) values[k] = !!v;
  for (const [k, v] of Object.entries(data.carousel_enhancements ?? {})) values[k] = !!v;
  for (const [k, v] of Object.entries(data.enhancements_catalog ?? {})) values[k] = !!v;

  for (const [k, v] of Object.entries(data.collection_enhancements ?? {})) {
    values[k] = v === "OPT_IN";
  }

  values.flexible_advantage_plus_creative = data.flexible_advantage_plus_creative === true;

  return values;
}

function valuesToSettings(
  values: Record<string, boolean>,
  toggles: { format_key: string; toggle_key: string }[],
): Partial<CreativeEnhancementsData> {
  const grouped: Record<string, string[]> = {};
  for (const t of toggles) {
    if (!grouped[t.format_key]) grouped[t.format_key] = [];
    grouped[t.format_key].push(t.toggle_key);
  }

  const mapFor = (formatKey: string) =>
    Object.fromEntries((grouped[formatKey] ?? []).map((k) => [k, values[k] === true])) as EnhancementMap;

  return {
    image_enhancements: mapFor("single_image"),
    video_enhancements: mapFor("single_video"),
    carousel_enhancements: mapFor("carousel"),
    enhancements_catalog: mapFor("catalog"),
    collection_enhancements: Object.fromEntries(
      (grouped.collection ?? []).map((k) => [k, values[k] ? "OPT_IN" : "OPT_OUT"]),
    ) as Record<string, "OPT_IN" | "OPT_OUT">,
    flexible_advantage_plus_creative: values.flexible_advantage_plus_creative === true,
  };
}

export function CreativeEnhancementsStep({ data, onChange }: CreativeEnhancementsStepProps) {
  const { data: config } = useEnhancementConfig();

  const values = useMemo(() => settingsToValues(data), [data]);

  const showSitelinks = useMemo(() => {
    return Object.entries(values).some(
      ([key, val]) => (key.includes("site_links") || key.includes("site_extensions")) && val,
    );
  }, [values]);

  const handleChange = useCallback(
    (key: string, value: boolean) => {
      if (!config) return;
      const nextValues = { ...values, [key]: value };
      onChange(valuesToSettings(nextValues, config.toggles));
    },
    [config, values, onChange],
  );

  const handleBatchChange = useCallback(
    (updates: Record<string, boolean>) => {
      if (!config) return;
      const nextValues = { ...values, ...updates };
      onChange(valuesToSettings(nextValues, config.toggles));
    },
    [config, values, onChange],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Creative Enhancements</CardTitle>
          <CardDescription>
            Set default Advantage+ creative enhancements for new ads. All enhancements are OFF by default.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnhancementTogglesPanel values={values} onChange={handleChange} onBatchChange={handleBatchChange} />
        </CardContent>
      </Card>

      {showSitelinks && (
        <SitelinksSection
          sitelinks={data.sitelinks ?? []}
          onChange={(sitelinks) => onChange({ sitelinks })}
        />
      )}
    </div>
  );
}
